import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ExpenditureDto,
  ExpenditureDetailDto,
  ExpenditureListResponse,
  ExpenditureListParams,
  CreateExpenditureRequest,
  VoidRowRequest,
  TriageItem,
  RowVersion,
  SortInboxExpenditureDto,
  UpdateExpenditureRequest,
} from '@signalvote/shared';
import { updateExpenditureRequest } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, staleVersion } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { throwStagedRowError } from './useFinanceContributions';
import {
  optimisticallyPatchSortInboxRow,
  rollbackSortInbox,
  markConfirmed,
  markPendingReview,
  markVoided,
  markReported,
  markUnreported,
  patchExpenditureCategory,
  patchExpenditureSavedDates,
  reconcileSortInboxRow,
  reconcileSortInboxRowVersion,
  type SortInboxSnapshot,
} from './sortInboxOptimistic';
import { useRowWrite } from '@signalvote/data';
import { useOptimisticStatusFlip } from './optimisticStatusFlip';
import { scheduleCloseHubRefresh } from './closeHubRefresh';

/**
 * Kind predicate selecting an Expenditure row in the sort-inbox cache — the row
 * identity the optimistic patch and the concurrency-token write-back both key on.
 */
const expenditureRow = (expenditureId: string) => (item: TriageItem): boolean =>
  item.kind === 'Expenditure' && item.expenditure.id === expenditureId;

/**
 * onSuccess reconcile for the optimistic expenditure mutations: write the server's
 * freshly-bumped `concurrencyVersion` (off the mutation response) into the row's
 * sort-inbox cache entry so a rapid FOLLOW-UP action on the SAME row sends the
 * current token, not the stale one that 409s. Still used directly by the
 * non-status-flip `useUpdateExpenditure` (its variables-dependent category patch
 * is a genuinely different shape, so it stays hand-written).
 */
function reconcileExpenditureVersion(
  queryClient: QueryClient,
  expenditureId: string,
  version: RowVersion | null | undefined,
): void {
  reconcileSortInboxRowVersion(queryClient, expenditureRow(expenditureId), version);
}

/**
 * onSettled invalidations for a status-only expenditure flip (confirm / unconfirm
 * / mark-reported / unmark-reported): the row's detail + the expenditures list +
 * the earliest-staged-date default + the sort inbox. `contacts.all` and
 * `obligations()` are deliberately omitted — a status flip is aggregate-neutral,
 * unlike void/unvoid below.
 */
function invalidateExpenditureStatusFlip(
  queryClient: QueryClient,
  expenditureId: string,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.detail(expenditureId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
  // A confirm/void/mark-reported flip changes the filing set, so the close-hub aggregates must
  // refresh: the filing preflight (hard blockers + fee nudge), finance-health (readiness pips), and
  // the close-hub period. These are server-side recomputes, so they're TRAILING-DEBOUNCED — a rapid
  // sweep of check-offs coalesces to a single refresh instead of one per click (the cheap
  // per-row/list invalidations above stay immediate, so the row flip + triage count feel instant).
  scheduleCloseHubRefresh(queryClient);
}

/**
 * onSettled invalidations for an expenditure void/unvoid: everything a status flip
 * refreshes PLUS `contacts.all` (excluding/restoring shifts the payee's totals)
 * and `obligations()` (it can arm/cancel a daily filing obligation — GitHub #81).
 */
function invalidateExpenditureVoid(
  queryClient: QueryClient,
  expenditureId: string,
): void {
  invalidateExpenditureStatusFlip(queryClient, expenditureId);
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
}

// ============================================================================
// Query Hooks
// ============================================================================

function buildExpenditureListUrl(params: ExpenditureListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.contactId) searchParams.set('contactId', params.contactId);
  if (params.category) searchParams.set('category', params.category);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  return `/api/finance/expenditures?${searchParams.toString()}`;
}

export function useFinanceExpenditures(params: ExpenditureListParams) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.expenditures.list(params),
    queryFn: () =>
      apiJson<ExpenditureListResponse>(buildExpenditureListUrl(params), {
        error: 'Failed to fetch expenditures',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateExpenditure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExpenditureRequest) =>
      apiJson<ExpenditureDto>('/api/finance/expenditures', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to create expenditure'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
      // The ledger + inbox render from the sort-inbox query; without this a
      // manually created expenditure only shows after a page reload.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
      // GitHub #81 — a $5k+ day (or a $20k rapid contribution) creates/re-arms a
      // FilingObligation server-side; refresh the obligations bar (in-window banner +
      // daily/rapid strip) so it appears without a page reload.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
    },
  });
}

// ============================================================================
// BSW-014: Staged-page detail panel hooks
// ============================================================================

/** The fields an expenditure edit re-sends unchanged, read off the row the edit lands on. */
export type EditableExpenditure = Pick<
  SortInboxExpenditureDto,
  'id' | 'concurrencyVersion' | 'category' | 'purpose' | 'authorizedDate' | 'incurredDate'
>;

/**
 * The fields an expenditure edit changes. Category and purpose it leaves out are re-sent as the
 * row has them when the edit runs, and so is the other event date of an `updateEventDates` edit.
 * Payee, amount, date and the debt and loan links it leaves out are not sent, which the server
 * reads as unchanged.
 */
export type ExpenditureEdit = Partial<Omit<UpdateExpenditureRequest, 'concurrencyVersion'>>;

export function useUpdateExpenditure() {
  const queryClient = useQueryClient();

  return useRowWrite<
    'Expenditure',
    RowVersion,
    ExpenditureEdit,
    ExpenditureDto,
    { previous: SortInboxSnapshot } | undefined,
    EditableExpenditure
  >(
    {
      entity: 'Expenditure',
      send: async (id, concurrencyVersion, edit, row) => {
        const eventDates = edit.updateEventDates
          ? { authorizedDate: row.authorizedDate, incurredDate: row.incurredDate }
          : {};
        const response = await apiResponse(`/api/finance/expenditures/${id}`, {
          method: 'PATCH',
          json: updateExpenditureRequest({
            category: row.category,
            purpose: row.purpose,
            ...eventDates,
            ...edit,
            concurrencyVersion,
          }),
        });
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to update expenditure');
        }
        return response.json() as Promise<ExpenditureDto>;
      },
    },
    {
      // Root cause #3 — the close-ledger category `<select>` reads its value straight
      // off the sort-inbox cache with no draft state, so a successful save would keep
      // showing the OLD category (visibly snapping back) until the full refetch below
      // commits. Patch ONLY the edited row's own category on click so the new value
      // shows instantly; an edit that leaves the category alone patches nothing. The
      // server stays the source of truth for all derived numbers — the
      // invalidate-on-settle below reconciles them.
      onMutate: async ({ row, category }) =>
        category === undefined
          ? undefined
          : { previous: await optimisticallyPatchSortInboxRow(queryClient, expenditureRow(row.id), patchExpenditureCategory(category)) },
      onError: (_err, _input, context) => {
        rollbackSortInbox(queryClient, context?.previous);
      },
      // The saved dates land in the cached row with its new token, so the date cells show
      // the save before the refetch. The invalidate-on-settle below still reconciles every
      // derived value.
      onSuccess: (data, { row }) => {
        reconcileExpenditureVersion(queryClient, row.id, data.concurrencyVersion);
        reconcileSortInboxRow(queryClient, expenditureRow(row.id), patchExpenditureSavedDates(data));
      },
      // Reconcile on SETTLE (success AND rejection) so a rejected edit still refetches
      // the fresh concurrencyVersion and the server-derived values land — mirroring the
      // other optimistic expenditure mutations. KEEP every invalidation: the sort-inbox
      // one also refreshes the NeedsAction feed / File-gate counts, so narrowing it would
      // risk a stale "Ready to file" over unfinished work.
      onSettled: (_data, _error, { row }) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.detail(row.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
        // ATL-002/006: expenditures ride in the all-time ledger; an inline
        // purpose edit from the reopened row must refresh the inbox.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
        // GitHub #81 — an amount/date edit changes the day's combined gross; refresh the
        // obligations bar so the daily strip appears/clears without a reload.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      },
    },
  );
}

// ULL-005 (design doc D2/D9): the expenditure ✓-uncheck on the All-Time
// Ledger. POSTs /api/finance/expenditures/{id}/unconfirm (ULL-002), the
// inverse of the born-Pending confirm flip — Confirmed → Pending, the row
// stays an Expenditure (no kind-morph). Mirror of `useUnconfirmContribution`.
//
// Distinct error envelope from the BSW-014 PATCH/void endpoints (which
// throw `StagedRowUpdateError`): the (un)confirm endpoints return their own
// `{ code }` payload from `ExpenditureUnconfirmErrorCodes`
// (`ExpenditureNotFound` → 404, `ConcurrencyConflict` → 409, everything else
// → 400). Surfacing it as a typed error class keeps the ledger's
// refusal-to-copy mapping (`IsReported`, concurrency) structurally distinct
// from the staged-row "modified elsewhere" message. (RFR-006 dropped the
// `RefundLinked` refusal — no Expenditure is ever a refund's half.)
//
// Named for the confirm/unconfirm FAMILY (not `…UnconfirmError`): when the
// born-Pending `useConfirmExpenditure` consumer lands it shares the same
// `ExpenditureConfirm/UnconfirmErrorCodes` envelope and reuses this class —
// mirroring how `ContributionConfirmError` backs both contribution flips.
export class ExpenditureConfirmError extends Error {
  code: string;
  status: number;
  constructor(
    code: string,
    status: number,
    message: string,
    /** The row's version on a 409 stale write, read by `useRowWrite`. */
    readonly currentVersion: number | null = null,
  ) {
    super(message);
    this.name = 'ExpenditureConfirmError';
    this.code = code;
    this.status = status;
  }
}

// ULL-005: the expenditure ✓-uncheck on the All-Time Ledger — Confirmed →
// Pending, the inverse of useConfirmExpenditure. Status-only flip, so contacts.all
// is deliberately omitted (mirrors useUnconfirmContribution).
export function useUnconfirmExpenditure() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Expenditure',
      send: (id, concurrencyVersion) =>
        apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/unconfirm`, {
          method: 'POST',
          json: { concurrencyVersion },
          error: (body, response) =>
            new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to uncheck expenditure', staleVersion(body)),
        }),
    },
    patch: markPendingReview,
    invalidateKeys: invalidateExpenditureStatusFlip,
  });
}

// ULL-003: the born-Pending → Confirmed flip for an expenditure on the
// All-Time Ledger. POSTs /api/finance/expenditures/{id}/confirm (ULL-002) —
// the path bank-classified (`BankTransactionClassifyAsExpenditureService`)
// and receipt-captured (`ReceiptCaptureService`) expenditures take to become
// filable. Inverse of `useUnconfirmExpenditure`; shares the same
// `ExpenditureConfirmError` envelope (the consumer the hook-family comment
// above anticipated). The backend refuses Reported + refund-FK-linked rows
// (D9) with `{ code }` payloads the ledger maps to treasurer copy.
// ULL-003: the born-Pending → Confirmed flip for an expenditure on the All-Time
// Ledger — the path bank-classified + receipt-captured expenditures take to
// become filable. Inverse of useUnconfirmExpenditure; status-only, aggregates
// untouched.
export function useConfirmExpenditure() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Expenditure',
      send: (id, concurrencyVersion) =>
        apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/confirm`, {
          method: 'POST',
          json: { concurrencyVersion },
          error: (body, response) =>
            new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to confirm expenditure', staleVersion(body)),
        }),
    },
    patch: markConfirmed,
    invalidateKeys: invalidateExpenditureStatusFlip,
  });
}

// AL EDI delta-export (design §6): the manual "mark as reported" carve-out for
// an expenditure the treasurer entered directly in the AL FCPA portal. Flips
// Confirmed → Reported so our rules see it without us re-exporting it (which
// would duplicate it on the append-only portal — ADR-020). Mirror of
// `useMarkContributionReported`; reuses the `ExpenditureConfirmError` envelope
// (wire codes from `ExpenditureMarkReportedErrorCodes`). The optional `note` is
// dropped by JSON.stringify when undefined.
// AL EDI delta-export: the manual "mark as reported" carve-out for an expenditure
// the treasurer entered directly in the AL FCPA portal (Confirmed → Reported).
// Mirror of useMarkContributionReported; the optional `note` is dropped by
// JSON.stringify when undefined.
export function useMarkExpenditureReported() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Expenditure',
      send: (id, concurrencyVersion, { note }: { note?: string }) =>
        apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/mark-reported`, {
          method: 'POST',
          json: { concurrencyVersion, note },
          error: (body, response) =>
            new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to mark this expenditure as reported', staleVersion(body)),
        }),
    },
    patch: markReported,
    invalidateKeys: invalidateExpenditureStatusFlip,
  });
}

// AL EDI delta-export (design §6): reverse a manual mark
// (Reported → Confirmed) for an expenditure, returning it to the export pool.
// The audited, provenance-gated carve-out of ADR-007.
// AL EDI delta-export: reverse a manual mark (Reported → Confirmed) for an
// expenditure, returning it to the export pool. Status-only flip.
export function useUnmarkExpenditureReported() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Expenditure',
      send: (id, concurrencyVersion) =>
        apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/unmark-reported`, {
          method: 'POST',
          json: { concurrencyVersion },
          error: (body, response) =>
            new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to undo the mark-reported', staleVersion(body)),
        }),
    },
    patch: markUnreported,
    invalidateKeys: invalidateExpenditureStatusFlip,
  });
}

// Stamp the row "Not reported" (Done/Voided) on click; the aggregates it shifts
// (contacts / obligations) stay server-truth-driven via the onSettled refetch,
// which (ATL-006) also drops the expenditure from the all-time inbox queue.
export function useVoidExpenditure() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Expenditure',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(`/api/finance/expenditures/${id}/void`, {
          method: 'POST',
          json: { concurrencyVersion } satisfies VoidRowRequest,
        });
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to void expenditure');
        }
        return response.json() as Promise<ExpenditureDto>;
      },
    },
    patch: markVoided,
    invalidateKeys: invalidateExpenditureVoid,
  });
}

// ADR-016 un-void — restores a Voided expenditure to Pending (re-review). The
// inverse of `useVoidExpenditure`; mirror of `useUnvoidContribution`. The
// restore button lives in the cancellation chooser dialog — this hook is its
// data path.
// ADR-016 un-void — restores a Voided expenditure to Pending (re-review). The
// inverse of useVoidExpenditure; same POST + body + invalidation set.
export function useUnvoidExpenditure() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Expenditure',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(`/api/finance/expenditures/${id}/unvoid`, {
          method: 'POST',
          json: { concurrencyVersion } satisfies VoidRowRequest,
        });
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to restore expenditure');
        }
        return response.json() as Promise<ExpenditureDto>;
      },
    },
    patch: markPendingReview,
    invalidateKeys: invalidateExpenditureVoid,
  });
}

// ADR-016 single-row Delete — permanently removes a non-Reported expenditure
// (a mis-entry that never happened). Mirror of `useDeleteContribution`: 204 (no
// payload), concurrency token as a query param, the `StagedRowUpdateError`
// envelope carrying `DeleteErrorCodes`.
export function useDeleteExpenditure() {
  const queryClient = useQueryClient();

  return useRowWrite<'Expenditure', RowVersion, object, void>(
    {
      entity: 'Expenditure',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(
          `/api/finance/expenditures/${id}?concurrencyVersion=${concurrencyVersion}`,
          { method: 'DELETE' },
        );
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to delete expenditure');
        }
        // 204 No Content — no payload.
      },
    },
    {
      onSuccess: (_data, { row }) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.detail(row.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
        // GitHub #81 — deleting the row removes it from the day's gross, which can cancel
        // its daily obligation; refresh the obligations bar without a reload.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      },
    },
  );
}
